// frontend_react/src/pages/main/EventRegister.jsx

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import {
  fetchEventById,
  registerToPublicEvent,
  getPublicEventIcsUrl,
} from "../../services/calendarApi";
import logger from "../../utils/logger";

export default function EventRegister() {
  const { eventUuid } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [eventLoading, setEventLoading] = useState(true);

  const [form, setForm] = useState({
    email: "",
    firstname: "",
    lastname: "",
    player_number: "",
    nb_participants: 1,
    guest_participants: [],
    rgpd_accepted: false,
    newsletter_opt_in: false,
  });

  const [status, setStatus] = useState({
    loading: false,
    ok: false,
    error: "",
    registration_uuid: null,
  });

  const participantsCount = Number(form.nb_participants || 1);

  const extraCount = useMemo(() => {
    return Math.max(0, participantsCount - 1);
  }, [participantsCount]);

  const unitPrice = Number(event?.payment_amount_ttc || 0);
  const totalPrice = unitPrice * participantsCount;

  useEffect(() => {
    const loadEvent = async () => {
      setEventLoading(true);

      try {
        const { data } = await fetchEventById(eventUuid);
        setEvent(data);
      } catch (err) {
        logger.error(err);
        setStatus((prev) => ({
          ...prev,
          error: "Impossible de charger l’événement.",
        }));
      } finally {
        setEventLoading(false);
      }
    };

    if (eventUuid) loadEvent();
  }, [eventUuid]);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const setGuestParticipant = (index, key, value) => {
    const next = [...(form.guest_participants || [])];

    next[index] = {
      firstname: "",
      lastname: "",
      email: "",
      player_number: "",
      ...(next[index] || {}),
      [key]: value,
    };

    setField("guest_participants", next);
  };

  const formatDateTime = (start, end) => {
    if (!start) return "Date à confirmer";

    const startDate = new Date(start);
    const endDate = end ? new Date(end) : null;

    const date = startDate.toLocaleDateString("fr-FR", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    });

    const startHour = startDate.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    });

    const endHour = endDate
      ? endDate.toLocaleTimeString("fr-FR", {
          hour: "2-digit",
          minute: "2-digit",
        })
      : null;

    return endHour
      ? `${date} • ${startHour} - ${endHour}`
      : `${date} • ${startHour}`;
  };

  const downloadIcs = () => {
    if (!eventUuid) return;
    window.open(getPublicEventIcsUrl(eventUuid), "_blank", "noopener,noreferrer");
  };

  const submit = async (eventSubmit) => {
    eventSubmit.preventDefault();

    setStatus({
      loading: true,
      ok: false,
      error: "",
      registration_uuid: null,
    });

    try {
      const payload = {
        email: form.email.trim(),
        firstname: form.firstname.trim(),
        lastname: form.lastname.trim() || null,
        player_number: form.player_number.trim() || null,
        nb_participants: Number(form.nb_participants || 1),
        guest_participants: (form.guest_participants || []).map((guest) => ({
          firstname: (guest.firstname || "").trim(),
          lastname: (guest.lastname || "").trim(),
          email: (guest.email || "").trim(),
          player_number: (guest.player_number || "").trim(),
        })),
        guest_emails: (form.guest_participants || [])
          .map((guest) => (guest.email || "").trim())
          .filter(Boolean),
        rgpd_accepted: !!form.rgpd_accepted,
        newsletter_opt_in: !!form.newsletter_opt_in,
      };

      const { data } = await registerToPublicEvent(eventUuid, payload);

      setStatus({
        loading: false,
        ok: true,
        error: "",
        registration_uuid: data?.registration_uuid || null,
      });
    } catch (err) {
      logger.error(err);

      setStatus({
        loading: false,
        ok: false,
        error:
          err?.response?.data?.error ||
          err?.message ||
          "Erreur lors de l’inscription.",
        registration_uuid: null,
      });
    }
  };

  if (status.ok) {
    return (
      <PageWrapper>
        <ContentWrapper>
          <FormSection>
            <SectionHeader>
              <SectionBadge>Inscription confirmée</SectionBadge>
              <h1>Merci, ton inscription est enregistrée.</h1>
              <p>
                Tu peux ajouter l’événement à ton calendrier ou revenir à la liste
                des événements.
              </p>
            </SectionHeader>

            {status.registration_uuid && (
              <InlineInfo>Référence : {status.registration_uuid}</InlineInfo>
            )}

            <Actions>
              <SubmitButton type="button" onClick={downloadIcs}>
                Télécharger / Ajouter au calendrier
              </SubmitButton>

              <GhostButton type="button" onClick={() => navigate("/events")}>
                Voir les autres événements
              </GhostButton>
            </Actions>
          </FormSection>
        </ContentWrapper>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <ContentWrapper>
        <FormSection>
          <SectionHeader>
            <SectionBadge>Événement</SectionBadge>
            <h1>{eventLoading ? "Chargement..." : event?.title || "Événement"}</h1>

            {!eventLoading && (
              <>
                <p>{formatDateTime(event?.start_timestamp, event?.end_timestamp)}</p>
                <p>{event?.location || "Lieu à confirmer"}</p>
              </>
            )}
            <p>
              Places : {event?.total_participants || 0}
              {event?.max_participants ? `/${event.max_participants}` : ""}
            </p>
          </SectionHeader>
        </FormSection>

        <FormWrapper onSubmit={submit}>
          <FormSection>
            <SectionHeader>
              <SectionBadge>Participant principal</SectionBadge>
              <h4>Coordonnées</h4>
            </SectionHeader>

            <FieldsGrid>
              <Field>
                <label>Email *</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                  required
                  placeholder="nom@domaine.com"
                />
              </Field>

              <Field>
                <label>Prénom *</label>
                <input
                  value={form.firstname}
                  onChange={(e) => setField("firstname", e.target.value)}
                  required
                  placeholder="Prénom"
                />
              </Field>

              <Field>
                <label>Nom</label>
                <input
                  value={form.lastname}
                  onChange={(e) => setField("lastname", e.target.value)}
                  placeholder="Nom"
                />
              </Field>
              <Field>
                <label>Numéro de joueur</label>
                <input
                  value={form.player_number}
                  onChange={(e) => setField("player_number", e.target.value)}
                  placeholder="Identifiant : Pokémon, Magic, One Piece, Lorcana, ..."
                />
              </Field>
            </FieldsGrid>
          </FormSection>

          <FormSection>
            <SectionHeader>
              <SectionBadge>Participants</SectionBadge>
              <h4>Nombre de places</h4>
            </SectionHeader>

            <FieldsGrid>
              <Field>
                <label>Nombre de participants *</label>
                <input
                  type="number"
                  min="1"
                  value={form.nb_participants}
                  onChange={(e) => setField("nb_participants", e.target.value)}
                  required
                />
              </Field>

            {Array.from({ length: extraCount }).map((_, index) => (
              <GuestBlock key={index}>
                <SectionHeader>
                  <SectionBadge>Invité #{index + 1}</SectionBadge>
                  <h4>Informations facultatives</h4>
                </SectionHeader>

                <FieldsGrid>
                  <Field>
                    <label>Prénom</label>
                    <input
                      value={form.guest_participants?.[index]?.firstname || ""}
                      onChange={(e) =>
                        setGuestParticipant(index, "firstname", e.target.value)
                      }
                      placeholder="Prénom"
                    />
                  </Field>

                  <Field>
                    <label>Nom</label>
                    <input
                      value={form.guest_participants?.[index]?.lastname || ""}
                      onChange={(e) =>
                        setGuestParticipant(index, "lastname", e.target.value)
                      }
                      placeholder="Nom"
                    />
                  </Field>

                  <Field>
                    <label>Email</label>
                    <input
                      type="email"
                      value={form.guest_participants?.[index]?.email || ""}
                      onChange={(e) =>
                        setGuestParticipant(index, "email", e.target.value)
                      }
                      placeholder="invite@domaine.com"
                    />
                  </Field>

                  <Field>
                    <label>Numéro de joueur</label>
                    <input
                      value={form.guest_participants?.[index]?.player_number || ""}
                      onChange={(e) =>
                        setGuestParticipant(index, "player_number", e.target.value)
                      }
                      placeholder="Identifiant : Pokémon, Magic, One Piece, Lorcana, ..."
                    />
                  </Field>
                </FieldsGrid>
              </GuestBlock>
            ))}
            </FieldsGrid>
          </FormSection>

          <FormSection>
            <SectionHeader>
              <SectionBadge>Consentements</SectionBadge>
              <h4>RGPD & newsletter</h4>
            </SectionHeader>

            <ConsentList>
              <ConsentRow>
                <input
                  type="checkbox"
                  checked={form.rgpd_accepted}
                  onChange={(e) => setField("rgpd_accepted", e.target.checked)}
                  required
                />
                <span>J’accepte la politique RGPD *</span>
              </ConsentRow>

              <ConsentRow>
                <input
                  type="checkbox"
                  checked={form.newsletter_opt_in}
                  onChange={(e) => setField("newsletter_opt_in", e.target.checked)}
                />
                <span>Je souhaite recevoir la newsletter Queen&apos;s Games</span>
              </ConsentRow>
            </ConsentList>
          </FormSection>

          {event?.payment_required && (
            <InlineInfo>
              Paiement requis : {event.payment_amount_ttc || "montant à confirmer"} {event.payment_currency || "EUR"}
            </InlineInfo>
          )}

          {event?.payment_required && (
            <FormSection>
              <SectionHeader>
                <SectionBadge>Paiement</SectionBadge>
                <h4>Paiement à réaliser</h4>
              </SectionHeader>

              <InlineInfo>
                Prix : {unitPrice.toFixed(2)} {event.payment_currency || "EUR"} ×{" "}
                {participantsCount} participant(s) ={" "}
                <strong>
                  {totalPrice.toFixed(2)} {event.payment_currency || "EUR"}
                </strong>
              </InlineInfo>

              {event.payment_url ? (
                <SubmitButton
                  type="button"
                  onClick={() =>
                    window.open(event.payment_url, "_blank", "noopener,noreferrer")
                  }
                >
                  Procéder au paiement
                </SubmitButton>
              ) : (
                <InlineInfo>
                  Le paiement en ligne sera bientôt disponible.
                </InlineInfo>
              )}
            </FormSection>
          )}

          {status.error && <PanelMessage $error>{status.error}</PanelMessage>}

          <Actions>
            <SubmitButton type="submit" disabled={status.loading}>
              {status.loading ? "Inscription..." : "Valider l’inscription"}
            </SubmitButton>

            <GhostButton type="button" onClick={downloadIcs}>
              Télécharger .ics
            </GhostButton>
          </Actions>
        </FormWrapper>
      </ContentWrapper>
    </PageWrapper>
  );
}

const PageWrapper = styled.main`
  min-height: 100dvh;
  width: 100%;
  padding: 1rem;
  box-sizing: border-box;
  background: #0b1328;
  color: #f7f7f7;
`;

const ContentWrapper = styled.div`
  width: min(100%, 620px);
  margin: 0 auto;
  display: grid;
  gap: 0.95rem;
`;

const FormWrapper = styled.form`
  display: grid;
  gap: 0.95rem;
`;

const FormSection = styled.section`
  padding: 1rem;
  border-radius: 16px;
  background: #202a43;
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const SectionHeader = styled.div`
  margin-bottom: 0.9rem;

  h1,
  h4 {
    margin: 0.45rem 0 0;
    font-weight: 950;
  }

  h1 {
    font-size: clamp(1.25rem, 4vw, 1.65rem);
  }

  h4 {
    font-size: 1rem;
  }

  p {
    margin: 0.45rem 0 0;
    opacity: 0.78;
    line-height: 1.45;
  }
`;

const SectionBadge = styled.span`
  display: inline-block;
  width: fit-content;
  padding: 0.3rem 0.65rem;
  border-radius: 999px;
  background: #fff9ea;
  color: #0b1328;
  font-size: 0.72rem;
  font-weight: 950;
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
`;

const Field = styled.div`
  display: grid;
  gap: 0.35rem;

  label {
    font-size: 0.78rem;
    font-weight: 850;
    opacity: 0.82;
  }

  input {
    width: 100%;
    min-height: 42px;
    border: none;
    border-radius: 10px;
    padding: 0.75rem;
    background: #fff9ea;
    color: #0b1328;
    font-weight: 750;
    box-sizing: border-box;
    outline: none;
  }

  input:focus {
    box-shadow: 0 0 0 3px rgba(255, 249, 234, 0.22);
  }
`;

const ConsentList = styled.div`
  display: grid;
  gap: 0.55rem;
`;

const ConsentRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  min-height: 42px;
  padding: 0.65rem 0.75rem;
  border-radius: 10px;
  background: rgba(255, 249, 234, 0.1);
  color: #fff9ea;
  font-size: 0.86rem;
  font-weight: 800;
  cursor: pointer;

  input {
    flex: 0 0 auto;
    width: 16px;
    height: 16px;
    margin: 0;
    accent-color: #fff9ea;
  }

  span {
    line-height: 1.25;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 0.65rem;

  @media (max-width: 720px) {
    flex-direction: column;
  }
`;

const SubmitButton = styled.button`
  flex: 1;
  border: none;
  border-radius: 12px;
  padding: 0.95rem 1rem;
  background: #fff9ea;
  color: #0b1328;
  font-weight: 950;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? 0.65 : 1)};
`;

const GhostButton = styled(SubmitButton)`
  background: rgba(255, 249, 234, 0.1);
  color: #fff9ea;
  border: 1px solid rgba(255, 249, 234, 0.35);
`;

const InlineInfo = styled.div`
  padding: 0.75rem 0.9rem;
  border-radius: 12px;
  background: rgba(255, 249, 234, 0.1);
  color: #fff9ea;
  font-size: 0.85rem;
  font-weight: 750;
  line-height: 1.4;
`;

const PanelMessage = styled.div`
  padding: 0.85rem 1rem;
  border-radius: 12px;
  font-weight: 850;
  background: ${({ $error }) =>
    $error ? "rgba(180, 40, 40, 0.9)" : "rgba(40, 140, 80, 0.9)"};
`;

const GuestBlock = styled.div`
  display: grid;
  gap: 0.75rem;
  padding: 0.85rem;
  border-radius: 14px;
  background: rgba(255, 249, 234, 0.08);
`;